// Basic Info Tab Component
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { ImageUpload } from '../../../components/ui/image-upload';
import { Plus, X, Star, Tag, Megaphone } from 'lucide-react';
import { statIconOptions } from './constants';

export const BasicInfoTab = ({ 
  formData, 
  setFormData, 
  editions, 
  topicInput, 
  setTopicInput,
  addStat,
  updateStat,
  removeStat,
  addTopic,
  removeTopic
}) => {
  return (
    <div className="space-y-4">
      {/* Header Banner Section */}
      <div className="p-4 rounded-lg bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Megaphone size={18} className="text-primary" />
            <Label className="text-base font-semibold">Banner Sopra il Titolo</Label>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.header_banner_enabled || false}
              onChange={(e) => setFormData({ ...formData, header_banner_enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="header-banner-toggle"
            />
            <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
        
        {formData.header_banner_enabled && (
          <div className="space-y-4 mt-4 pt-4 border-t border-white/10">
            <div className="space-y-2">
              <Label>Testo del Banner</Label>
              <Textarea
                value={formData.header_banner_text || ''}
                onChange={(e) => setFormData({ ...formData, header_banner_text: e.target.value })}
                placeholder="Es: In collaborazione con..."
                className="bg-white/5 border-white/10 min-h-16"
                data-testid="header-banner-text"
              />
            </div>
            <div className="space-y-2">
              <Label>Immagine del Banner</Label>
              <ImageUpload
                value={formData.header_banner_image || ''}
                onChange={(url) => setFormData({ ...formData, header_banner_image: url })}
                label="Immagine Banner"
              />
              <p className="text-xs text-muted-foreground">
                Consigliato: logo o immagine orizzontale del partner/sponsor
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Titolo *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            className="bg-white/5 border-white/10"
            data-testid="input-title"
          />
        </div>
        <div className="space-y-2">
          <Label>Edizione *</Label>
          <Select
            value={formData.edition_id}
            onValueChange={(value) => setFormData({ ...formData, edition_id: value })}
            required
          >
            <SelectTrigger className="bg-white/5 border-white/10">
              <SelectValue placeholder="Seleziona edizione" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              {editions.map((edition) => (
                <SelectItem key={edition.id} value={edition.id}>
                  {edition.title} ({edition.year})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Descrizione</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="bg-white/5 border-white/10 min-h-24"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Data Inizio</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="bg-white/5 border-white/10"
          />
        </div>
        <div className="space-y-2">
          <Label>Data Fine (opzionale)</Label>
          <Input
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            className="bg-white/5 border-white/10"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Orario Inizio</Label>
          <Input
            type="time"
            value={formData.event_start_time}
            onChange={(e) => setFormData({ ...formData, event_start_time: e.target.value })}
            className="bg-white/5 border-white/10"
            placeholder="09:00"
          />
        </div>
        <div className="space-y-2">
          <Label>Orario Fine</Label>
          <Input
            type="time"
            value={formData.event_end_time}
            onChange={(e) => setFormData({ ...formData, event_end_time: e.target.value })}
            className="bg-white/5 border-white/10"
            placeholder="18:00"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Slug (opzionale)</Label>
          <Input
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="auto-generato dal titolo"
            className="bg-white/5 border-white/10"
          />
          <p className="text-xs text-muted-foreground">Lascia vuoto per generarlo automaticamente</p>
        </div>
        <div className="space-y-2">
          <Label>Stato</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger className="bg-white/5 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              <SelectItem value="draft">Bozza</SelectItem>
              <SelectItem value="publish">Pubblicato</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Section */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star size={18} className="text-primary" />
            <Label className="text-base font-semibold">Statistiche</Label>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addStat}>
            <Plus size={14} className="mr-1" />
            Aggiungi
          </Button>
        </div>
        <div className="space-y-3">
          {formData.stats.map((stat, index) => (
            <div key={`stat-${stat.label || index}`} className="glass rounded-lg p-3 flex items-center gap-3">
              <Select
                value={stat.icon}
                onValueChange={(value) => updateStat(index, 'icon', value)}
              >
                <SelectTrigger className="w-32 bg-white/5 border-white/10 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {statIconOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon size={14} />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={stat.value}
                onChange={(e) => updateStat(index, 'value', e.target.value)}
                placeholder="500+"
                className="w-24 bg-white/5 border-white/10 h-9"
              />
              <Input
                value={stat.label}
                onChange={(e) => updateStat(index, 'label', e.target.value)}
                placeholder="Partecipanti"
                className="flex-1 bg-white/5 border-white/10 h-9"
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => removeStat(index)} className="h-9 w-9 p-0 text-destructive">
                <X size={14} />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Topics Section */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Tag size={18} className="text-primary" />
          <Label className="text-base font-semibold">Topics</Label>
        </div>
        <div className="flex gap-2 mb-3">
          <Input
            value={topicInput}
            onChange={(e) => setTopicInput(e.target.value)}
            placeholder="Aggiungi un topic..."
            className="bg-white/5 border-white/10"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
          />
          <Button type="button" variant="outline" onClick={addTopic}>
            <Plus size={14} />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.topics.map((topic, index) => (
            <span key={`topic-${topic}`} className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm flex items-center gap-2">
              {topic}
              <button type="button" onClick={() => removeTopic(index)} className="hover:text-white">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
